import React, { useCallback, useRef, useState } from 'react';
import moment from 'moment';
import { Popover, PopoverAnchor, PopoverContent } from '../../shadcn/components/ui/popover';
import { cn } from '../../shadcn/lib/utils';
import Calendar from './calendar/Calendar';
import Icon from '../icon/Icon';

const DATE_FORMAT = 'D MMM YYYY';

export const formatDisplayDate = (timestamp: number | undefined): string => moment.utc(timestamp).format(DATE_FORMAT);

const ACCEPTED_FORMATS = [DATE_FORMAT, 'MM/DD/YYYY', 'YYYY-MM-DD', 'M/D/YYYY', 'MMM D YYYY'];

export const parseTypedDate = (input: string): number | undefined => {
  const parsed = moment.utc(input, ACCEPTED_FORMATS);
  return parsed.isValid() ? parsed.valueOf() : undefined;
};

export const SimpleDatePicker = ({
  value,
  onChange,
  disabled,
}: {
  value: number | undefined;
  onChange: (timestamp: number) => void;
  disabled: boolean | undefined;
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayValue = formatDisplayDate(value);

  const handleDateSelect = useCallback(
    (timestamp: number) => {
      onChange(timestamp);
      setIsEditing(false);
      setOpen(false);
    },
    [onChange],
  );

  const handleInputFocus = useCallback(() => {
    if (disabled) return;
    setInputValue(value ? formatDisplayDate(value) : '');
    setIsEditing(true);
    setOpen(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [disabled, value]);

  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (e.relatedTarget?.closest('[data-radix-popper-content-wrapper]')) return;
      const trimmed = e.target.value.trim();
      if (trimmed) {
        const parsed = parseTypedDate(trimmed);
        if (parsed !== undefined) onChange(parsed);
      }
      setIsEditing(false);
      setOpen(false);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const parsed = parseTypedDate(inputValue.trim());
        if (parsed !== undefined) onChange(parsed);
        setIsEditing(false);
        setOpen(false);
        inputRef.current?.blur();
      }
      if (e.key === 'Escape') {
        setIsEditing(false);
        setOpen(false);
        inputRef.current?.blur();
      }
    },
    [inputValue, onChange],
  );

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen && document.activeElement === inputRef.current) return;
    setOpen(nextOpen);
  }, []);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <label
          className={cn(
            'flex items-center gap-[4px] h-[36px] w-[120px] px-[8px] border border-solid rounded text-[13px]',
            disabled
              ? 'border-venn-grey-40 text-venn-grey-60 cursor-default'
              : 'border-venn-grey-30 text-venn-black cursor-text',
          )}
        >
          <input
            ref={inputRef}
            type="text"
            value={isEditing ? inputValue : displayValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            style={{ outline: 'none', boxShadow: 'none' }}
            className={cn(
              'flex-1 min-w-0 bg-transparent text-[13px] p-0 border-0',
              disabled ? 'text-venn-grey-60 cursor-default' : 'text-venn-black cursor-text',
            )}
          />
          <Icon
            type="calendar"
            className={cn('text-[12px] flex-shrink-0', disabled ? 'text-venn-grey-60' : 'text-venn-black')}
          />
        </label>
      </PopoverAnchor>
      <PopoverContent align="start" className="w-auto p-[4px]">
        <Calendar value={value} onChange={handleDateSelect} />
      </PopoverContent>
    </Popover>
  );
};
